import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, Zap } from "lucide-react";
import { AppStoreBadge } from "./AppStoreBadge";
import { GooglePlayBadge } from "./GooglePlayBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Sin backend configurado la waitlist queda en modo "próximamente" (no rompe el CTA).
// Al setear REACT_APP_BACKEND_URL en build, el formulario se reactiva automáticamente.
const ENABLED = Boolean(BACKEND_URL);
const API = `${BACKEND_URL}/api`;

export const Waitlist = ({ t, lang }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!ENABLED) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t.waitlist.invalidEmail);
      return;
    }
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/waitlist`, { email, name: name || null });
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(err.response?.status === 409 ? t.waitlist.duplicate : t.waitlist.error);
    }
  };

  return (
    <section id="waitlist" data-testid="waitlist-section" className="py-24 md:py-32 bg-[#0A0A0A] border-t border-white/5 relative overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-orange-500/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="relative max-w-2xl mx-auto px-6 lg:px-8 text-center">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-orange-500 mb-4">{t.waitlist.kicker}</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-white">{t.waitlist.title}</h2>
          <p className="mt-4 text-base text-zinc-400 leading-relaxed">{t.waitlist.subtitle}</p>

          <div className="mt-8 flex flex-col items-center gap-3">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <AppStoreBadge t={t} lang={lang} className="h-[56px]" testId="waitlist-app-store-badge" />
              <GooglePlayBadge t={t} lang={lang} className="h-[56px]" testId="waitlist-google-play-badge" />
            </div>
            <p className="text-xs text-zinc-500">{t.stores.note}</p>
          </div>

          <p className="mt-10 text-sm text-zinc-400">{t.waitlist.androidNote}</p>

          {status === "success" ? (
            <div data-testid="waitlist-success-message" className="mt-10 inline-flex items-center gap-3 bg-orange-500/10 border border-orange-500/30 text-orange-400 px-6 py-4 rounded-2xl font-medium">
              <CheckCircle2 className="w-5 h-5" />
              {t.waitlist.success}
            </div>
          ) : (
            <form onSubmit={submit} data-testid="waitlist-form" className="mt-6 flex flex-col gap-4 max-w-md mx-auto">
              <input
                data-testid="waitlist-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.waitlist.namePlaceholder}
                disabled={!ENABLED}
                className="bg-black/50 border border-white/10 text-white placeholder:text-zinc-500 rounded-lg px-4 py-3 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <input
                data-testid="waitlist-email-input"
                type="email"
                required={ENABLED}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.waitlist.emailPlaceholder}
                disabled={!ENABLED}
                className="bg-black/50 border border-white/10 text-white placeholder:text-zinc-500 rounded-lg px-4 py-3 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {error && <p data-testid="waitlist-error-message" className="text-sm text-red-400 text-left">{error}</p>}
              <button
                data-testid="waitlist-submit-button"
                type="submit"
                disabled={status === "loading" || !ENABLED}
                className="inline-flex items-center justify-center gap-2 bg-[#F7931A] text-black font-semibold px-6 py-3.5 rounded-lg hover:bg-[#E88210] transition-all shadow-[0_0_20px_rgba(247,147,26,0.4)] hover:shadow-[0_0_30px_rgba(247,147,26,0.6)] disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none"
              >
                <Zap className="w-4 h-4" fill="currentColor" />
                {!ENABLED ? t.waitlist.comingSoon : status === "loading" ? t.waitlist.submitting : t.waitlist.submit}
              </button>
              {!ENABLED && (
                <p data-testid="waitlist-comingsoon-note" className="text-sm text-zinc-500">
                  {t.waitlist.comingSoonNote}
                </p>
              )}
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
